/**
 * Settings Module — clinic-admin only.
 *
 * On desktop the sidebar sits fixed on the left. On phones (< 768px) the
 * sidebar collapses behind a "Menu ▼" button that opens a full-height
 * drawer. Picking any tab auto-closes the drawer so the content takes the
 * full viewport width.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import {
  Settings, Building2, Users, MapPin, Pen, ListChecks, ShieldCheck,
  MessageCircle, Clock, CalendarClock, Upload, User, Printer, Stamp,
  Stethoscope, Menu, X, ChevronDown, Crown, Puzzle, Receipt, Mail,
} from 'lucide-react';
import ClinicDetailsTab from './ClinicDetailsTab';
import StaffSettingsTab from './StaffSettingsTab';
import BranchesTab from './BranchesTab';
import RolesPermissionsTab from './RolesPermissionsTab';
import ClinicGroupTab from './ClinicGroupTab';
import MySignatureTab from './MySignatureTab';
import MySealTab from './MySealTab';
import MyProfileTab from './MyProfileTab';
import SecurityPrivacyTab from './SecurityPrivacyTab';
import ConnectWhatsAppTab from './ConnectWhatsAppTab';
import SettingsIntegrationsTab from './SettingsIntegrationsTab';
import ClinicHoursTab from './ClinicHoursTab';
import StaffScheduleTab from './StaffScheduleTab';
import DataImportTab from './DataImportTab';
import PrintTemplatesTab from './PrintTemplatesTab';
import BlankAudiogramTemplate from './templates/BlankAudiogramTemplate';
import BillingPreferencesTab from './BillingPreferencesTab';
import ScheduledReportsTab from './ScheduledReportsTab';
import ServiceCatalogPage from '../billing/ServiceCatalogPage';
import ReferralDoctorsTab from './ReferralDoctorsTab';
import { useAuth } from '../../AuthContext';
import { NoPermission } from '../../shell/NoPermission';

const CO_OWNER_BLOCKED = ['clinic', 'staff', 'roles', 'branches', 'clinic-group', 'import', 'billing-prefs', 'services'];

export default function SettingsModule() {
  const { user } = useAuth();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // `founder` is a platform-level role that also administers the platform tenant
  // ("audinexa-platform") — it needs the same clinic-admin surface (branches,
  // security, staff, etc.) as clinic_owner + super_admin, otherwise the founder
  // has no path to enable MFA / configure the platform tenant. Bug found
  // 2026-08-31 when MFA enforcement started blocking founder access.
  const isAdmin = ['clinic_owner', 'clinic_admin', 'super_admin', 'founder'].includes(user?.role);
  const isOwner = ['clinic_owner', 'super_admin', 'founder'].includes(user?.role);
  const isMarketing = user?.role === 'marketing';
  const canManageCatalog = ['clinic_owner', 'super_admin', 'accounts', 'founder'].includes(user?.role);
  // Integrations hub is intentionally hidden from clinic_owner for now — the
  // page will be surfaced to clinic staff after we announce it publicly and
  // build trust in the "credentials present / not present" transparency
  // story. Until then only platform staff see the tab + route (2026-02).
  const isPlatformStaff = ['super_admin', 'founder'].includes(user?.role);
  // Franchise co-owners cannot open chain-wide settings.
  const isCoOwner = !!user?.is_co_owner;
  const blocked = (path) => isCoOwner && CO_OWNER_BLOCKED.includes(path);

  // Auto-close mobile drawer whenever the route changes.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Build the visible-to-this-user nav list. Central definition so both the
  // sidebar and the mobile drawer read from the same source of truth (and
  // the mobile top bar can look up the current tab's label).
  const navItems = useMemo(() => {
    const items = [];
    if (isAdmin) {
      items.push(
        { to: '/settings/clinic',           icon: Building2,     label: 'Clinic Details',    testid: 'settings-nav-clinic' },
        { to: '/settings/hours',            icon: Clock,         label: 'Clinic Hours',      testid: 'settings-nav-hours' },
        { to: '/settings/staff',            icon: Users,         label: 'Staff Settings',    testid: 'settings-nav-staff' },
        ...(isOwner ? [{ to: '/settings/roles', icon: ShieldCheck, label: 'Roles & Permissions', testid: 'settings-nav-roles' }] : []),
        { to: '/settings/staff-schedule',   icon: CalendarClock, label: 'Doctor Schedule',   testid: 'settings-nav-staff-schedule' },
        { to: '/settings/referral-doctors', icon: Stethoscope,   label: 'Referral Doctors',  testid: 'settings-nav-referral-doctors' },
        { to: '/settings/branches',         icon: MapPin,        label: 'Branches',          testid: 'settings-nav-branches' },
        { to: '/settings/clinic-group',     icon: Crown,         label: 'Clinic Group',      testid: 'settings-nav-clinic-group' },
        { to: '/settings/security',         icon: ShieldCheck,   label: 'Security & Privacy',testid: 'settings-nav-security' },
        { to: '/settings/connect',          icon: MessageCircle, label: 'Connect (WhatsApp)',testid: 'settings-nav-connect' },
        { to: '/settings/import',           icon: Upload,        label: 'Data Import',       testid: 'settings-nav-import' },
        { to: '/settings/billing-prefs',    icon: Receipt,       label: 'Billing Preferences', testid: 'settings-nav-billing-prefs' },
        { to: '/settings/scheduled-reports', icon: Mail,         label: 'Scheduled Reports', testid: 'settings-nav-scheduled-reports' },
        { to: '/settings/templates',        icon: Printer,       label: 'Print Templates',   testid: 'settings-nav-templates' },
      );
    }
    if (canManageCatalog) {
      items.push({ to: '/settings/services', icon: ListChecks, label: 'Services & Packages', testid: 'settings-nav-services' });
    }
    if (isPlatformStaff) {
      items.push({ to: '/settings/integrations', icon: Puzzle, label: 'Integrations', testid: 'settings-nav-integrations' });
    }
    if (isMarketing) {
      items.push({ to: '/settings/referral-doctors', icon: Stethoscope, label: 'Referral Doctors', testid: 'settings-nav-referral-doctors' });
    }
    // Divider marker — rendered as a horizontal rule when present.
    if (items.length > 0) items.push({ divider: true });
    items.push(
      { to: '/settings/profile',   icon: User,  label: 'My Profile',   testid: 'settings-nav-profile' },
      { to: '/settings/signature', icon: Pen,   label: 'My Signature', testid: 'settings-nav-signature' },
      { to: '/settings/seal',      icon: Stamp, label: 'My Seal',      testid: 'settings-nav-seal' },
    );
    return isCoOwner ? items.filter((it) => it.divider || !CO_OWNER_BLOCKED.includes(it.to.replace('/settings/', ''))) : items;
  }, [isAdmin, isOwner, canManageCatalog, isPlatformStaff, isMarketing, isCoOwner]);

  // Label for the mobile top bar — mirrors the currently active tab.
  const activeTab = navItems.find((it) => !it.divider && location.pathname.startsWith(it.to));
  const activeLabel = activeTab?.label || 'Settings';
  const ActiveIcon = activeTab?.icon || Settings;

  return (
    <div className="h-full flex flex-col md:flex-row bg-slate-50" data-testid="settings-module">
      {/* Mobile top bar — visible on < md */}
      <div className="md:hidden sticky top-0 z-20 flex items-center justify-between gap-2 bg-white border-b border-slate-200 px-3 py-2">
        <button
          onClick={() => setDrawerOpen(true)}
          data-testid="settings-mobile-menu-toggle"
          className="flex items-center gap-2 flex-1 min-w-0 px-2 py-1.5 text-sm font-semibold text-slate-700 bg-slate-50 border border-slate-200 rounded hover:bg-slate-100 active:bg-slate-200"
        >
          <Menu size={14} />
          <ActiveIcon size={14} className="text-indigo-600 shrink-0" />
          <span className="truncate flex-1 text-left">{activeLabel}</span>
          <ChevronDown size={14} className="text-slate-400 shrink-0" />
        </button>
      </div>

      {/* Desktop sidebar — hidden on < md */}
      <aside className="hidden md:flex w-56 bg-white border-r border-slate-200 p-3 flex-col shrink-0">
        <div className="flex items-center gap-2 px-2 py-1 mb-3">
          <Settings size={16} className="text-slate-500" />
          <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Settings</div>
        </div>
        <NavList items={navItems} />
      </aside>

      {/* Mobile drawer — full-height slide-in from the left */}
      {drawerOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex" data-testid="settings-mobile-drawer">
          {/* Backdrop */}
          <button
            aria-label="Close menu"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-slate-900/50"
          />
          {/* Drawer */}
          <div className="relative w-72 max-w-[85vw] bg-white h-full shadow-xl flex flex-col animate-in slide-in-from-left duration-150">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <Settings size={16} className="text-slate-500" />
                <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Settings</div>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                data-testid="settings-mobile-drawer-close"
                className="p-1 hover:bg-slate-100 rounded"
              >
                <X size={16} className="text-slate-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              <NavList items={navItems} />
            </div>
          </div>
        </div>
      )}

      {/* Content area — full width on mobile, remaining space on desktop */}
      <main className="flex-1 overflow-auto min-w-0">
        <Routes>
          <Route index element={<Navigate to={isCoOwner ? 'hours' : isAdmin ? 'clinic' : (canManageCatalog ? 'services' : 'profile')} replace />} />
          {isAdmin && <Route path="clinic"   element={blocked('clinic') ? <NoPermission /> : <ClinicDetailsTab />} />}
          {isAdmin && <Route path="hours"    element={<ClinicHoursTab />} />}
          {isAdmin && <Route path="staff"    element={blocked('staff') ? <NoPermission /> : <StaffSettingsTab />} />}
          {isAdmin && <Route path="staff-schedule" element={<StaffScheduleTab />} />}
          {(isAdmin || isMarketing) && <Route path="referral-doctors" element={<ReferralDoctorsTab />} />}
          {isAdmin && <Route path="branches" element={blocked('branches') ? <NoPermission /> : <BranchesTab />} />}
          <Route path="roles" element={isOwner && !isCoOwner ? <RolesPermissionsTab /> : <NoPermission />} />
          {isAdmin && <Route path="clinic-group" element={blocked('clinic-group') ? <NoPermission /> : <ClinicGroupTab />} />}
          {isAdmin && <Route path="security" element={<SecurityPrivacyTab />} />}
          {isAdmin && <Route path="connect"  element={<ConnectWhatsAppTab />} />}
          {isPlatformStaff && <Route path="integrations" element={<SettingsIntegrationsTab />} />}
          {isAdmin && <Route path="import"   element={blocked('import') ? <NoPermission /> : <DataImportTab />} />}
          {isAdmin && <Route path="billing-prefs" element={blocked('billing-prefs') ? <NoPermission /> : <BillingPreferencesTab />} />}
          {isAdmin && <Route path="scheduled-reports" element={<ScheduledReportsTab />} />}
          {isAdmin && <Route path="templates" element={<PrintTemplatesTab />} />}
          {isAdmin && <Route path="templates/audiogram" element={<BlankAudiogramTemplate />} />}
          <Route path="services" element={canManageCatalog && !isCoOwner ? <ServiceCatalogPage /> : <NoPermission />} />
          <Route path="profile"   element={<MyProfileTab />} />
          <Route path="signature" element={<MySignatureTab />} />
          <Route path="seal"      element={<MySealTab />} />
        </Routes>
      </main>
    </div>
  );
}

function NavList({ items }) {
  return (
    <nav className="flex flex-col">
      {items.map((it, i) => {
        if (it.divider) return <div key={`div-${i}`} className="my-2 border-t border-slate-100" />;
        return (
          <SideLink key={it.to} to={it.to} icon={<it.icon size={14} />} label={it.label} testid={it.testid} />
        );
      })}
    </nav>
  );
}

function SideLink({ to, icon, label, testid }) {
  return (
    <NavLink
      to={to}
      data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded transition ${
          isActive ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' : 'text-slate-600 hover:bg-slate-50'
        }`
      }
    >
      {icon}
      {label}
    </NavLink>
  );
}
